import { Fragment } from 'react';
import { ScaleLinear, ScaleTime } from 'd3-scale';

import { AreaClosed, LinePath, Bar } from '@visx/shape';
import { curveMonotoneX } from '@visx/curve';
import { Group } from '@visx/group';
import { LinearGradient } from '@visx/gradient';

import { hasNoNeighbors } from './utils';

type AreaSeriesProps = {
  data: { date: Date; value: number | null }[];
  fillColor: string;
  strokeColor: string;
  opacity?: number;
  valueScale: ScaleLinear<number, number>;
  dateScale: ScaleTime<number, number>;
};

export const AreaSeries = (props: AreaSeriesProps) => {
  const { data, fillColor, strokeColor, opacity = 1, valueScale, dateScale } = props;
  const gradientId = `gradient-${fillColor}-${opacity}`;
  return (
    <Group>
      <LinearGradient
        id={gradientId}
        from={fillColor}
        to={fillColor}
        fromOpacity={opacity}
        toOpacity={0.3}
      />
      <AreaClosed
        data={data}
        x={({ date }) => dateScale(date) ?? 0}
        y0={({ value }) => valueScale(value ?? 0)}
        y1={() => valueScale(0)}
        defined={({ value }) => value !== null}
        yScale={valueScale}
        fill={`url(#${gradientId}`}
        fillOpacity={opacity}
        curve={curveMonotoneX}
      />
      <LinePath
        data={data}
        x={({ date }) => dateScale(date) ?? 0}
        y={({ value }) => valueScale(value ?? 0)}
        defined={({ value }) => value !== null}
        stroke={strokeColor}
        strokeOpacity={opacity}
        curve={curveMonotoneX}
      />
      {data
        .filter((_, i) => hasNoNeighbors(data, i))
        .map(({ date, value }) => {
          if (value === null) {
            return null;
          }
          return (
            <Fragment key={`dot-${date.toISOString()}`}>
              <Bar
                x={dateScale(date) - 1}
                y={valueScale(value)}
                width={2}
                height={valueScale(0) - valueScale(value)}
                fill={fillColor}
              />
              <Bar
                x={dateScale(date) - 1}
                y={valueScale(value) - 2}
                width={2}
                height={2}
                fill={strokeColor}
              />
            </Fragment>
          );
        })}
    </Group>
  );
};
